<template>
    <div id="ApplyForJobButton" class="w-full whitespace-nowrap">
        <client-only>
            <CButton v-if="preview" full size="large" variant="success">
                {{ $t('jobView.jobApplyButton.interested', job.lang || 'sk') }}
            </CButton>

            <template v-else>
                <CButton
                    v-if="jobOwner"
                    full
                    icon="edit"
                    size="large"
                    :to="{
                        name: 'profile-activity-job-slug',
                        params: { slug: job.slug || job.id }
                    }"
                    variant="primary-150"
                >
                    {{ $t('jobView.jobApplyButton.edit', job.lang || 'sk') }}
                </CButton>

                <CButton v-else-if="hasAlreadyApplied" disabled full size="large">
                    {{ $t('jobView.jobApplyButton.waiting', job.lang || 'sk') }}
                </CButton>

                <CButton v-else full size="large" variant="success" @click="openJobRequestModal">
                    {{ $t('jobView.jobApplyButton.interested', job.lang || 'sk') }}
                </CButton>
            </template>
        </client-only>
    </div>
</template>

<script setup lang="ts">
import { computed, nextTick, onMounted, ref } from 'vue'
import { useRoute } from 'vue-router'
import cloneDeep from 'lodash-es/cloneDeep'
import { useModal } from 'vue-final-modal'

import type { Job } from '#shared/types/Job'
import { JobRequest } from '#shared/types/JobRequest'
import type { Org } from '#shared/types/Org'
import { UserProfile } from '#shared/types/User'
import RegistrationNotCompletedModal from '@/components/common/modals/RegistrationNotCompletedModal.vue'
import SendJobRequestModal from '@/components/jobs/process/modals/SendJobRequestModal.vue'
import { useAuth } from '@/composables/auth/useAuth'
import { useExponea } from '@/composables/exponea/useExponea'
import { useJobRequests } from '@/composables/jobRequests/useJobRequests'
import { useCheese } from '@/composables/useCheese'

const props = defineProps({
    job: {
        type: Object as () => Job,
        required: true
    },
    org: {
        type: Object as () => Org,
        required: true
    },
    jobOwner: {
        type: Boolean,
        default: false
    },
    preview: {
        type: Boolean,
        default: false
    }
})

const store = useApplicationStore()
const route = useRoute()
const { canApply, getUserId } = useAuth()
const { trackExponeaEvent } = useExponea()
const { getUsersActiveJobRequests } = useJobRequests()
const { track: trackCheese } = useCheese()

const activeJobRequests = ref<JobRequest[]>([])
const userProfile = ref<UserProfile | undefined>(undefined)

const hasAlreadyApplied = computed((): boolean => {
    return activeJobRequests.value.length > 0
})

const fetchData = async () => {
    // Visitors who aren't signed in have no applications, and Firestore rules refuse the query without a uid
    if (props.job.id && getUserId()) {
        try {
            activeJobRequests.value = await getUsersActiveJobRequests(props.job.id)
        } catch (error) {
            console.error('[ApplyForJobButton] Failed to load own job requests:', error)
        }
    }

    userProfile.value = cloneDeep(store.userProfile || {})

    if (route.query.toggleModal) {
        // Use nextTick to ensure modal is ready after component mount
        nextTick(() => {
            openJobRequestModal()
        })
    }
}

// Modal setup
const { open: openRegistrationNotCompletedModal } = useModal({
    component: RegistrationNotCompletedModal
})

const { open: openSendJobRequestModal } = useModal({
    component: SendJobRequestModal,
    attrs: {
        job: props.job,
        onClose(jobRequest?: any) {
            if (jobRequest) {
                activeJobRequests.value.push(jobRequest)
            }
        }
    }
})

const openJobRequestModal = async () => {
    if (props.preview || props.jobOwner || hasAlreadyApplied.value) return
    if (process.client) {
        console.log('Event tracked: job - openApplicationModal')
    }

    trackExponeaEvent({
        eventName: 'cta1_zaujem_o_job',
        userId: getUserId(),
        eventProperties: {
            job_id: props.job.id,
            job_name: props.job.title,
            job_organisation: props.org.title,
            job_location: props.job.location,
            job_location_remote: props.job.remote,
            job_type: props.job.type,
            job_salary_low: props.job.salaryMin,
            job_salary_high: props.job.salaryMax,
            job_salary_unit: props.job.salaryUnit,
            job_audience: props.job.who
        }
    })

    // Check if user can apply (logged in, email verified, rules accepted)
    const applyCheck = canApply()
    if (!applyCheck.canApply && applyCheck.reason !== 'not_logged_in') {
        // User is logged in but hasn't completed registration
        return openRegistrationNotCompletedModal()
    }

    // Note: Job application allows unregistered users, so we proceed even if not logged in
    try {
        await openSendJobRequestModal()
        trackCheese('job_application_started', { job_id: props.job.id || '' })
    } catch (error) {
        console.error('Error opening job request modal:', error)
    }
}

// Initialize data on component mount
onMounted(fetchData)
</script>
